import classNames from "classnames";
import { get, isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  computeUpsellPricing,
  isLumineisUpsells,
} from "utils/helpers/lumineisUpsells";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import DetailsModal from "./DetailsModal";
import UpsellsCard from "./UpsellsCard";

const RENFORT_DECISION = {
  ACCEPT: "accept",
  REFUSE: "refuse",
};

function View({
  onNext,
  onBack,
  isSubmitting = false,
  isPacks = false,
  upsells,
  selectedModules,
  handleModule,
  projectId,
  insurancePlanId,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const { t: tProject } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const [openDetails, setOpenDetails] = useState(null);
  const [renfortDecision, setRenfortDecision] = useState(() => {
    if (isPacks) return null;
    const selectedIds = get(selectedModules, insurancePlanId, []).map(
      (module) => get(module, "insurancePlan.id"),
    );
    const hasSelectedRenfort = (upsells || []).some((module) =>
      selectedIds.includes(get(module, "insurancePlan.id")),
    );
    return hasSelectedRenfort ? RENFORT_DECISION.ACCEPT : null;
  });

  const UPSELLS = t("UPSELLS", { returnObjects: true });
  const DETAIL = tProject("DETAIL", { returnObjects: true });
  const HEADER = isPacks ? UPSELLS.PACK_HEADER : UPSELLS.HEADER;

  const selected = get(selectedModules, insurancePlanId, []);
  const continueEnabled = Boolean(isPacks || renfortDecision);
  const renfortName = get(upsells, "0.insurancePlan.name") || "";

  const isModuleSelected = (module) =>
    Boolean(
      selected.find(
        ({ insurancePlan }) =>
          insurancePlan.id === get(module, "insurancePlan.id"),
      ),
    );

  const acceptRenfort = () => {
    setRenfortDecision(RENFORT_DECISION.ACCEPT);
    upsells.forEach((module) => {
      if (!isModuleSelected(module)) {
        handleModule({ module });
      }
    });
  };

  const refuseRenfort = () => {
    setRenfortDecision(RENFORT_DECISION.REFUSE);
    upsells.forEach((module) => {
      if (isModuleSelected(module)) {
        handleModule({ module });
      }
    });
  };

  const handleCardClick = (module) => {
    if (isPacks) {
      handleModule({ module });
      return;
    }

    const willSelect = !isModuleSelected(module);
    handleModule({ module });
    const selectedCount = upsells.filter(isModuleSelected).length;
    const nextCount = willSelect ? selectedCount + 1 : selectedCount - 1;
    setRenfortDecision(nextCount > 0 ? RENFORT_DECISION.ACCEPT : null);
  };

  const { costForModule } = useMemo(
    () => computeUpsellPricing({ modules: upsells, selectedModules: selected }),
    [upsells, selected],
  );

  const showPackDiscountNote =
    isPacks && isLumineisUpsells(upsells) && upsells.length >= 2;

  const renderModuleCard = (module) => {
    const supportingInformation = get(
      module,
      "insurancePlan.supportingInformation",
      [],
    );
    const supportingBenefits = (supportingInformation || []).flatMap(
      (item) => item.value || [],
    );
    const flagshipGuarantees = get(
      module,
      "insurancePlan.fields.flagshipGuarantees",
      [],
    );
    const benefits = !isEmpty(supportingBenefits)
      ? supportingBenefits
      : flagshipGuarantees;

    return (
      <UpsellsCard
        key={module?.id}
        notSelected={
          !get(selectedModules, insurancePlanId)?.find(
            ({ insurancePlan }) =>
              insurancePlan.id === get(module, "insurancePlan.id"),
          )
        }
        name={get(module, "insurancePlan.name") || ""}
        description={get(module, "insurancePlan.description") || ""}
        benefits={benefits}
        cost={costForModule(module)}
        currency={get(module, "currency")}
        perMonthLabel={UPSELLS.PER_MONTH}
        onAdd={() => handleCardClick(module)}
      />
    );
  };

  return (
    <Layout>
      <Header
        title={HEADER.TITLE}
        highlight={HEADER.HIGHLIGHT}
        subtitle={HEADER.SUBTITLE}
      />
      <div className="acoris-upsells">
        {showPackDiscountNote && (
          <div className="acoris-upsells__discount-info">
            {UPSELLS.DISCOUNT_NOTE}
          </div>
        )}
        <div className="acoris-upsells__list">
          {upsells.map(renderModuleCard)}
        </div>

        {!isPacks && (
          <div
            className="acoris-upsells__choices"
            role="radiogroup"
            aria-label={UPSELLS.CHOICE_LABEL}
          >
            <button
              type="button"
              role="radio"
              aria-checked={renfortDecision === RENFORT_DECISION.ACCEPT}
              className={classNames("acoris-upsells__choice", {
                "acoris-upsells__choice--selected":
                  renfortDecision === RENFORT_DECISION.ACCEPT,
              })}
              disabled={isSubmitting}
              onClick={acceptRenfort}
            >
              {t("UPSELLS.ACCEPT", { name: renfortName })}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={renfortDecision === RENFORT_DECISION.REFUSE}
              className={classNames("acoris-upsells__choice", {
                "acoris-upsells__choice--selected":
                  renfortDecision === RENFORT_DECISION.REFUSE,
              })}
              disabled={isSubmitting}
              onClick={refuseRenfort}
            >
              {UPSELLS.REFUSE}
            </button>
          </div>
        )}

        <div className="acoris-upsells__footer">
          <ActionButtons
            onBack={onBack}
            onNext={() =>
              onNext({
                ignore: !isPacks && renfortDecision === RENFORT_DECISION.REFUSE,
              })
            }
            nextText={UPSELLS.BUTTON.CONTINUE}
            backText={UPSELLS.BUTTON.BACK}
            nextProps={{
              loading: isSubmitting,
              disabled: isSubmitting || !continueEnabled,
            }}
          />
          {isPacks && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onNext({ ignore: true })}
              className={classNames("acoris-upsells__skip-link", {
                "acoris-upsells__skip-link--hidden": !isEmpty(
                  get(selectedModules, insurancePlanId),
                ),
              })}
            >
              {UPSELLS.SKIP}
            </button>
          )}
        </div>
      </div>

      {openDetails && (
        <DetailsModal
          centered
          open={openDetails}
          onClose={() => setOpenDetails(null)}
          filter={{
            where: {
              insurancePlan: { id: openDetails },
              project: { id: projectId },
            },
          }}
          translation={DETAIL.TEMPLATES.COMPARISON}
        />
      )}
    </Layout>
  );
}

export default View;
